import { Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import type { LatLng } from 'leaflet';
import { useLayoutEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';

const OFFSET_X = 10;
const OFFSET_Y = 10;
const PADDING = 8;
const DEFAULT_CARD_WIDTH = 280;
const DEFAULT_CARD_HEIGHT = 120;

interface Props {
  hoverAnchor?: LatLng | null;
  rightInset?: number;
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function computePlacement(
  p: { x: number; y: number },
  mapSize: { x: number; y: number },
  cardWidth: number,
  cardHeight: number,
  rightInset: number,
) {
  const maxRight = mapSize.x - rightInset;
  let left = p.x + OFFSET_X;
  let top = p.y + OFFSET_Y;

  // Flip horizontally: if card would overflow right, show to the left of anchor
  if (left + cardWidth > maxRight - PADDING) {
    left = p.x - OFFSET_X - cardWidth;
  }
  if (left < PADDING) {
    left = PADDING;
  }
  if (left + cardWidth > maxRight - PADDING) {
    left = maxRight - cardWidth - PADDING;
  }

  // Flip vertically: if card would overflow bottom, show above anchor
  if (top + cardHeight > mapSize.y - PADDING) {
    top = p.y - OFFSET_Y - cardHeight;
  }
  if (top < PADDING) {
    top = PADDING;
  }
  if (top + cardHeight > mapSize.y - PADDING) {
    top = mapSize.y - cardHeight - PADDING;
  }

  return { left, top };
}

function HoverCard({
  hoverAnchor,
  rightInset = 0,
  children,
  onMouseEnter,
  onMouseLeave,
}: Readonly<Props>) {
  const map = useMap();
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (!hoverAnchor || !cardRef.current) {
      if (!hoverAnchor) setCardSize(null);
      return;
    }
    const rect = cardRef.current.getBoundingClientRect();
    setCardSize({ width: rect.width, height: rect.height });
  }, [hoverAnchor]);

  const p = hoverAnchor ? map.latLngToContainerPoint(hoverAnchor) : null;
  if (!p) return null;

  const mapSize = map.getSize();
  const width = cardSize?.width ?? DEFAULT_CARD_WIDTH;
  const height = cardSize?.height ?? DEFAULT_CARD_HEIGHT;
  const { left, top } = computePlacement(p, mapSize, width, height, rightInset);

  return (
    <AnimatePresence>
      <Box
        ref={cardRef}
        data-testid="hover-card"
        component={motion.div}
        key="hover-card"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 0.9, scale: 1 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ duration: 0.12, ease: 'easeOut' }}
        sx={{
          position: 'absolute',
          left,
          top,
          zIndex: 1200,
          pointerEvents: 'auto',
          opacity: 0.9,
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </Box>
    </AnimatePresence>
  );
}

export default HoverCard;
